import roundOffTotal from "./roundOffTotal";

export const generateInvoiceHtml = (
  order,
  totals,
  user,
  exclusiveGST,
  roundOffType = "standardRoundOff",
) => {
  const escapeHTML = (str) => {
    if (typeof str !== "string") return str;
    return str.replace(
      /[&<>'"]/g,
      (tag) =>
        ({
          "&": "&amp;",
          "<": "&lt;",
          ">": "&gt;",
          "'": "&#39;",
          '"': "&quot;",
        })[tag] || tag,
    );
  };

  const {
    branchOrderNumber = "N/A",
    orderNumber = "N/A",
    orderType = "N/A",
    tableNumber = "",
    paymentType = "N/A",
    updatedAt = new Date(),
    orderItems = [],
    customerName = "",
    customerMobile = "",
    customerEmail = "",
  } = order;

  const {
    restaurantName = "MealBill Restaurant",
    branchName = "",
    branchAddress = "",
    fssaiNumber = "",
    gstNumber = "",
    logo = null,
    phone = "",
    email = "",
    receiptMessage = "Thank you for dining with us!",
  } = user;

  const safeBranchOrderNumber = escapeHTML(branchOrderNumber);
  const safeOrderNumber = escapeHTML(orderNumber);
  const safeOrderType = escapeHTML(orderType);
  const safeTableNumber = escapeHTML(tableNumber);
  const safePaymentType = escapeHTML(paymentType);
  const safeCustomerName = escapeHTML(customerName);
  const safeCustomerMobile = escapeHTML(customerMobile);
  const safeCustomerEmail = escapeHTML(customerEmail);

  const safeRestaurantName = escapeHTML(restaurantName);
  const safeBranchName = escapeHTML(branchName);
  const safeBranchAddress = escapeHTML(branchAddress);
  const safeFssaiNumber = escapeHTML(fssaiNumber);
  const safeGstNumber = escapeHTML(gstNumber);
  const safePhone = escapeHTML(phone);
  const safeEmail = escapeHTML(email);
  const safeReceiptMessage = escapeHTML(receiptMessage);

  const subTotal = parseFloat(totals?.subTotal || 0);
  const discount = parseFloat(totals?.discount || 0);
  const otherCharges = parseFloat(totals?.otherCharges || 0);
  const grandTotal = parseFloat(totals?.grandTotal || 0);
  const taxTotal = parseFloat(totals?.taxTotal || 0);

  const gst = exclusiveGST
    ? parseFloat(order.cgstCharges || 0) + parseFloat(order.sgstCharges || 0)
    : 0;
  const unrounded = subTotal - discount + otherCharges + gst;
  const rounded = roundOffTotal(unrounded, roundOffType);
  const roundOffValue = rounded - unrounded;

  const formatDate = (d) => {
    try {
      const dt = new Date(d);
      const dd = String(dt.getDate()).padStart(2, "0");
      const mm = String(dt.getMonth() + 1).padStart(2, "0");
      const yr = dt.getFullYear();
      return `${dd}/${mm}/${yr}`;
    } catch {
      return "";
    }
  };

  const validItems = orderItems.filter((item) => item.quantity > 0);

  let uniqueGstRates = 0;
  if (safeGstNumber && validItems.length > 0) {
    const rates = new Set();
    validItems.forEach((item) => {
      if (item.cgst !== undefined && (item.cgst > 0 || item.sgst > 0 || safeGstNumber)) {
        rates.add(item.cgst);
      }
    });
    uniqueGstRates = rates.size;
  }

  const SLOTS_MAX = 16;
  const SLOTS_TOTALS = 4;
  const SLOTS_TAX = uniqueGstRates > 0 ? (2 + uniqueGstRates) : 0;
  const SLOTS_FOOTER = 3;

  const pagesData = [];
  let remainingItems = [...validItems];
  
  let totalsAssigned = false;
  let taxSummaryAssigned = (uniqueGstRates === 0);
  let footerBottomAssigned = false;

  while (
    remainingItems.length > 0 ||
    !totalsAssigned ||
    !taxSummaryAssigned ||
    !footerBottomAssigned
  ) {
    let availableSlots = SLOTS_MAX;
    let pageItems = [];
    let pageHasTotals = false;
    let pageHasTax = false;
    let pageHasFooter = false;

    // 1. Assign items
    while (remainingItems.length > 0 && availableSlots > 0) {
      pageItems.push(remainingItems.shift());
      availableSlots--;
    }

    // 2. Can we fit totals?
    if (remainingItems.length === 0 && !totalsAssigned) {
      if (availableSlots >= SLOTS_TOTALS) {
        pageHasTotals = true;
        totalsAssigned = true;
        availableSlots -= SLOTS_TOTALS;
      }
    }

    // 3. Can we fit tax summary?
    if (totalsAssigned && !taxSummaryAssigned) {
      const taxRequires = SLOTS_TAX + SLOTS_FOOTER;
      if (availableSlots >= Math.min(taxRequires, SLOTS_MAX)) {
        pageHasTax = true;
        taxSummaryAssigned = true;
        availableSlots -= SLOTS_TAX;
      }
    }

    // 4. Can we fit footer bottom?
    if (taxSummaryAssigned && !footerBottomAssigned) {
      if (availableSlots >= SLOTS_FOOTER) {
        pageHasFooter = true;
        footerBottomAssigned = true;
        availableSlots -= SLOTS_FOOTER;
      }
    }

    pagesData.push({
      items: pageItems,
      hasTotals: pageHasTotals,
      hasTaxSummary: pageHasTax,
      hasFooterBottom: pageHasFooter,
    });
  }

  const renderItems = (items) => {
    return items
      .map((item) => {
        const itemPriceVal = item.itemPrice || item.price || 0;
        const itemTotalVal = item.quantity * itemPriceVal;
        return `
      <tr>
        <td>
          <span class="item-name">${escapeHTML(item.itemName)}</span>
          ${item.remarks ? `<span class="item-remarks">* ${escapeHTML(item.remarks)}</span>` : ""}
        </td>
        <td class="tc">${item.quantity}</td>
        <td class="tc">&#8377;${Number(itemPriceVal).toFixed(2)}</td>
        <td class="tr">&#8377;${Number(itemTotalVal).toFixed(2)}</td>
      </tr>`;
      })
      .join("");
  };

  const getTaxSummaryHTML = () => {
    if (!order.orderItems) return "";

    const gstMap = new Map();
    let itemTotal = 0;
    order.orderItems.forEach((item) => {
      if (item.quantity > 0) {
        itemTotal += item.itemTotal || (item.quantity * (item.itemPrice || item.price || 0));
        if (
          item.cgst !== undefined &&
          (item.cgst > 0 || item.sgst > 0 || safeGstNumber)
        ) {
          if (!gstMap.has(item.cgst)) {
            gstMap.set(item.cgst, []);
          }
          gstMap.get(item.cgst).push(item);
        }
      }
    });

    const keys = [...gstMap.keys()];
    if (keys.length === 0) return "";

    const chargeDifference = -(discount || 0);

    let html = `
      <div style="text-align: left; margin-top: 16px; margin-bottom: 16px; page-break-inside: avoid;">
        <p style="font-weight: 700; font-size: 11px; margin: 0 0 4px 0; color: #111827;">Tax Summary</p>
        <p style="font-size: 9px; margin: 0 0 12px 0; color: #6b7280;">GST ${safeGstNumber}</p>
    `;

    keys.forEach((key) => {
      const items = gstMap.get(key);
      let itemsTotalAmt = 0;
      items.forEach((val) => {
        const itemPrise = val.itemTotal || (val.quantity * (val.itemPrice || val.price || 0));
        itemsTotalAmt +=
          itemPrise +
          (itemTotal > 0 ? chargeDifference * (itemPrise / itemTotal) : 0);
      });

      const cgstRate = items[0]?.cgst || 0;
      const sgstRate = items[0]?.sgst || 0;
      const gstAmount = (cgstRate + sgstRate) / 100 + (exclusiveGST ? 0 : 1);

      const taxAmount = exclusiveGST
        ? itemsTotalAmt
        : itemsTotalAmt / gstAmount;

      const cgstVal = exclusiveGST
        ? (taxAmount * ((cgstRate + sgstRate) / 100)) / 2
        : (itemsTotalAmt - taxAmount) / 2;

      const sgstVal = cgstVal;

      html += `
        <table style="width: 100%; border-collapse: collapse; margin-bottom: 8px; font-size: 9px;">
          <thead>
            <tr style="border-bottom: 1px dashed #d1d5db; color: #4b5563;">
              <th style="text-align: left; padding: 4px 2px; font-weight: 600;">Taxable Amt</th>
              <th style="text-align: center; padding: 4px 2px; font-weight: 600;">CGST@${cgstRate}%</th>
              <th style="text-align: center; padding: 4px 2px; font-weight: 600;">SGST@${sgstRate}%</th>
            </tr>
          </thead>
          <tbody>
            <tr style="color: #374151;">
              <td style="text-align: left; padding: 4px 2px;">&#8377;${taxAmount.toFixed(2)}</td>
              <td style="text-align: center; padding: 4px 2px;">&#8377;${cgstVal.toFixed(2)}</td>
              <td style="text-align: center; padding: 4px 2px;">&#8377;${sgstVal.toFixed(2)}</td>
            </tr>
          </tbody>
        </table>
      `;
    });

    html += `</div>`;
    return html;
  };

  const htmlPages = pagesData
    .map((page, index) => {
      return `
  <div class="a5-page">
    <div class="top-banner">
      <svg width="100%" height="100%" viewBox="0 0 1000 40" preserveAspectRatio="none" style="display:block;width:100%;height:100%;">
        <rect width="1000" height="40" fill="#223447" />
        <path d="M750,0 L1000,0 L1000,40 L700,40 Z" fill="#F0583C" />
        <path d="M680,0 L710,0 L660,40 L630,40 Z" fill="#F0583C" fill-opacity="0.2" />
        <path d="M640,0 L660,0 L610,40 L590,40 Z" fill="#F0583C" fill-opacity="0.5" />
      </svg>
    </div>
    <div class="content-area">
      <div class="header-section">
        <div class="header-row">
          <div class="header-left-item">
            <h1 class="invoice-title">INVOICE</h1>
          </div>
          <div class="header-right-item">
            <div class="logo-section">
              ${logo ? `<img src="${logo}" class="logo-img" alt="Logo" crossorigin="anonymous" />` : `<div class="logo-placeholder">LOGO</div>`}
              <div class="rest-details">
                ${safeBranchName ? `<p class="rest-name">${safeBranchName}</p>` : ""}
              </div>
            </div>
          </div>
        </div>
        
        <div class="header-row">
          <div class="header-left-item">
            <div class="to-section">
              <div class="to-label">To:</div>
              <div class="customer-name">${safeCustomerName || "Walk-in Customer"}</div>
              ${safeCustomerMobile ? `<div>P: ${safeCustomerMobile}</div>` : ""}
              ${safeCustomerEmail ? `<div>E: ${safeCustomerEmail}</div>` : ""}
            </div>
          </div>
          <div class="header-right-item">
            <div class="meta-details">
              <div class="meta-row"><span class="meta-label">No.</span> #${safeBranchOrderNumber || safeOrderNumber}</div>
              <div class="meta-row"><span class="meta-label">Date:</span> ${formatDate(updatedAt)}</div>
              ${safeOrderType ? `<div class="meta-row"><span class="meta-label">Type:</span> ${safeOrderType}</div>` : ""}
              ${safePaymentType ? `<div class="meta-row"><span class="meta-label">Payment:</span> ${safePaymentType}</div>` : ""}
              ${safeTableNumber ? `<div class="meta-row"><span class="meta-label">Table:</span> ${safeTableNumber}</div>` : ""}
            </div>
          </div>
        </div>
      </div>

      ${
        page.items.length > 0
          ? `
      <table class="items-table">
        <colgroup>
          <col style="width: 50%;">
          <col style="width: 15%;">
          <col style="width: 17%;">
          <col style="width: 18%;">
        </colgroup>
        <thead>
          <tr>
            <th>Item Description</th>
            <th class="tc">Qty</th>
            <th class="tc">Price</th>
            <th class="tr">Total</th>
          </tr>
        </thead>
        <tbody>
          ${renderItems(page.items)}
        </tbody>
      </table>
      `
          : ""
      }

      ${
        page.hasTotals
          ? `
      <div class="totals-section">
        <div class="totals-box">
          <div class="total-row">
            <span>Sub Total</span>
            <span>&#8377;${Number(subTotal).toFixed(2)}</span>
          </div>
          ${
            discount > 0
              ? `
          <div class="total-row" style="color: #10b981;">
            <span>Discount (-)</span>
            <span>&#8377;${Number(discount).toFixed(2)}</span>
          </div>`
              : ""
          }
          ${
            exclusiveGST && taxTotal > 0
              ? `
          <div class="total-row">
            <span>GST Charges (+)</span>
            <span>&#8377;${Number(taxTotal).toFixed(2)}</span>
          </div>`
              : ""
          }

${
  otherCharges > 0
    ? `
          <div class="total-row">
            <span>Other Charges (+)</span>
            <span>&#8377;${Number(otherCharges).toFixed(2)}</span>
          </div>`
    : ""
} 

${
  parseFloat(roundOffValue.toFixed(2)) !== 0
    ? `
          <div class="total-row">
            <span>Round off (${roundOffValue > 0 ? "+" : "-"})</span>
            <span>&#8377;${Math.abs(Number(roundOffValue)).toFixed(2)}</span>
          </div>`
    : ""
} 

<div class="total-row grand">
            <span>Grand Total:</span>
            <span>&#8377;${Number(rounded).toFixed(2)}</span>
          </div>
          ${
            !exclusiveGST && taxTotal > 0
              ? `
          <div style="text-align: right; font-size: 9px; color: #9ca3af; margin-top: 4px;">
            (Incl. GST: &#8377;${Number(taxTotal).toFixed(2)})
          </div>`
              : ""
          }
        </div>
      </div>
      `
          : ""
      }

      ${page.hasTaxSummary ? getTaxSummaryHTML() : ""}

      ${
        page.hasFooterBottom
          ? `
      <div class="footer-bottom">
        <div class="footer-col">
          <p>${safeReceiptMessage}</p>
          <p>Visit Again!</p>
        </div>
        <div class="footer-col">
          <p>${safeBranchAddress}</p>
          ${safeGstNumber ? `<p>GSTIN: ${safeGstNumber}</p>` : ""}
          ${safeFssaiNumber ? `<p>FSSAI: ${safeFssaiNumber}</p>` : ""}
        </div>
        <div class="footer-col" style="padding-left: 20px;">
          ${safePhone ? `<div class="flex-between"><span>Phone:</span> <span>${safePhone}</span></div>` : ""}
          ${safeEmail ? `<div class="flex-between"><span>Email:</span> <span>${safeEmail}</span></div>` : ""}
          <div class="flex-between" style="margin-top: 8px;"><span>Powered by:</span> <p>MealBill</p></div>
        </div>
      </div>
      `
          : `<div style="flex: 1;"></div>`
      }
    </div>
    <div class="bottom-banner">
      <svg width="100%" height="100%" viewBox="0 0 1000 40" preserveAspectRatio="none" style="display:block;width:100%;height:100%;">
        <rect width="1000" height="40" fill="#223447" />
        <path d="M0,0 L300,0 L250,40 L0,40 Z" fill="#F0583C" />
        <path d="M320,0 L350,0 L300,40 L270,40 Z" fill="#F0583C" fill-opacity="0.2" />
        <path d="M370,0 L390,0 L340,40 L320,40 Z" fill="#F0583C" fill-opacity="0.5" />
      </svg>
    </div>
  </div>`;
    })
    .join("");

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Invoice #${safeBranchOrderNumber}</title>
  <style>
    @page { size: A5 portrait; margin: 0; }
    body, html { margin: 0; padding: 0; background: #fff; font-family: 'Inter', 'Segoe UI', Helvetica, Arial, sans-serif; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    
    .a5-page {
      width: 148mm;
      height: 210mm;
      position: relative;
      background: #ffffff;
      box-sizing: border-box;
      margin: 0 auto;
      overflow: hidden;
    }
    
    .top-banner { height: 28px; width: 100%; }
    .bottom-banner { height: 28px; width: 100%; position: absolute; bottom: 0; left: 0; }
    
    .content-area { padding: 32px 24px 40px 24px; display: flex; flex-direction: column; height: calc(210mm - 28px); box-sizing: border-box; }
    .header-section { display: flex; flex-direction: column; gap: 20px; margin-bottom: 32px; }
    .header-row { display: flex; justify-content: space-between; align-items: flex-start; width: 100%; }
    .header-left-item { display: flex; flex-direction: column; gap: 4px; }
    .header-right-item { display: flex; flex-direction: column; gap: 4px; align-items: flex-end; text-align: right; }
    .invoice-title { font-size: 25px; font-weight: 700; color: #F0583C; text-transform: uppercase; letter-spacing: 2px; margin: 0; }
    
    .to-section { font-size: 10px; color: #4b5563; line-height: 1.5; }
    .to-label { font-weight: 700; color: #111827; }
    .customer-name { font-weight: 700; color: #111827; text-transform: capitalize; }
    .logo-section { display: flex; align-items: center; gap: 8px; }
    .logo-img { height: 42px; width: auto; max-width: 120px; object-fit: contain; margin-top: 14px; }
    .logo-placeholder { height: 36px; width: 36px; border-radius: 4px; background-color: #e5e7eb; display: flex; align-items: center; justify-content: center; font-size: 8px; font-weight: 700; color: #9ca3af; }
    
    .rest-details { text-align: left; }
    .rest-name { font-weight: 700; font-size: 16px; color: #111827; margin: 0; line-height: 1.2; }
    .rest-branch { font-size: 9px; color: #6b7280; margin: 0; }
    
    .meta-details { font-size: 9px; color: #6b7280; display: flex; flex-direction: column; gap: 2px; text-align: right; }
    .meta-row { display: flex; justify-content: flex-end; gap: 4px; color: #272829; }
    .meta-label { font-weight: 700; color: #272829; text-transform: capitalize; }
    
    .items-table { width: 100%; border-collapse: collapse; margin-bottom: 24px; text-align: left; }
    .items-table th { padding: 8px 6px 12px 6px; font-size: 11px; font-weight: 600; color: #111827; text-transform: uppercase; border-top: 1px solid #e5e7eb; border-bottom: 1px solid #e5e7eb; }
    .items-table td { padding: 6px 6px; font-size: 11px; color: #374151; border-bottom: 1px solid #f3f4f6; vertical-align: top; }
    .item-name { color: #111827; margin-bottom: 2px; display: block; }
    .item-remarks { color: #6b7280; font-size: 10px; display: block; }
    .tc { text-align: center; }
    .tr { text-align: right; }

    .totals-section { display: flex; justify-content: flex-end; margin-bottom: 24px; }
    .totals-box { width: 220px; }
    .total-row { display: flex; justify-content: space-between; font-size: 11px; color: #272829; margin-bottom: 3px; }
    .total-row.grand { margin-top: 12px; padding-top: 6px; border-top: 1px solid #e5e7eb; font-size: 12px; font-weight: 700; color: #111827; }

    .footer-bottom { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 12px; font-size: 8px; color: #6b7280; margin-top: auto; padding-top: 16px; border-top: 1px solid #e5e7eb; }
    .footer-col p { margin: 0 0 2px 0; font-size: 10px; color: #272829; }
    .footer-col .col-title { font-weight: 700; color: #111827; margin-bottom: 6px; }
    .flex-between { display: flex; justify-content: space-between; width: 100%; }
    .flex-between span:first-child { font-weight: 700; color: #111827; margin-right: 4px; }
  </style>
</head>
<body>
  <div class="invoice-container">
    ${htmlPages}
  </div>
</body>
</html>`;
};
